import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'

export interface WorkspaceStep {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  description?: string
  badge?: string
  isDisabled?: boolean
}

interface WorkspaceSidebarProps {
  steps: WorkspaceStep[]
  currentStepKey: string
  onStepClick: (stepKey: string) => void
  header?: React.ReactNode
  footer?: React.ReactNode
}

export function WorkspaceSidebar({
  steps,
  currentStepKey,
  onStepClick,
  header,
  footer,
}: WorkspaceSidebarProps) {
  return (
    <aside className="flex h-full w-64 flex-col border-r bg-muted/30">
      {/* Header */}
      {header && (
        <div className="border-b px-4 py-4">
          {header}
        </div>
      )}

      {/* Steps */}
      <nav className="flex-1 space-y-1 p-2">
        {steps.map((step, index) => {
          const isCurrent = step.key === currentStepKey
          const Icon = step.icon

          return (
            <button
              key={step.key}
              onClick={() => !step.isDisabled && onStepClick(step.key)}
              disabled={step.isDisabled}
              className={cn(
                'group flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:pointer-events-none disabled:opacity-50',
                isCurrent && 'bg-accent text-accent-foreground',
                !isCurrent && 'text-muted-foreground'
              )}
            >
              {/* Step number indicator */}
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium',
                  isCurrent && 'border-primary bg-primary text-primary-foreground',
                  !isCurrent && 'border-muted-foreground/30 bg-background'
                )}
              >
                <span>{index + 1}</span>
              </div>

              {/* Label and description */}
              <div className="flex-1 space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'text-sm font-medium',
                    isCurrent && 'text-foreground',
                    !isCurrent && 'text-muted-foreground group-hover:text-foreground'
                  )}>
                    {step.label}
                  </span>
                  {step.badge && (
                    <Badge variant="secondary" className="text-xs">
                      {step.badge}
                    </Badge>
                  )}
                </div>
                {step.description && (
                  <p className="text-xs text-muted-foreground">
                    {step.description}
                  </p>
                )}
              </div>

              {/* Icon on the right */}
              <Icon className={cn(
                'h-4 w-4 shrink-0 mt-1',
                isCurrent ? 'text-primary' : 'text-muted-foreground/50'
              )} />
            </button>
          )
        })}
      </nav>

      {/* Footer */}
      {footer && (
        <div className="border-t px-4 py-4">
          {footer}
        </div>
      )}
    </aside>
  )
}
